/**
 * Qui peut voir un module de dashboard : la permission que l'API exige vraiment,
 * et le module de l'établissement dont il dépend.
 *
 * Le dashboard est PARTAGÉ par tout l'hôtel : un widget posé par la direction
 * s'affiche aussi chez la réception. Le registre web et le dashboard mobile tirent
 * donc la même conclusion de la même déclaration, d'où ce fichier dans `domain/`
 * (le mobile ne peut pas atteindre `pages/dashboard/modules/registry.ts`).
 *
 * Module **feuille** : aucun import, comme `modulesDashboard.ts`.
 */

export interface AccesModule {
    /** Permission exigée par l'API. Un tableau vaut « au moins une ». */
    permission?: string | readonly string[];
    /** Flag `admin_has_*` de l'établissement (ex. `admin_has_housekeeping`). */
    facilityFeature?: string;
}

const MENAGE = 'admin_has_housekeeping';

/**
 * Les modules ménage, alignés sur `routes/api.php` :
 * - `stats/overview` et `board` : housekeeping.view ;
 * - `stats/agents` (performance nominative) : housekeeping.report ;
 * - `dashboard/maintenance` : housekeeping.maintenance.view ;
 * - `dashboard/lost-found` : housekeeping.lostfound.view.
 */
export const ACCES_MODULES_MENAGE = {
    housekeeping_overview: { permission: 'housekeeping.view', facilityFeature: MENAGE },
    housekeeping_rooms: { permission: 'housekeeping.view', facilityFeature: MENAGE },
    housekeeping_staff: { permission: 'housekeeping.report', facilityFeature: MENAGE },
    maintenance: { permission: 'housekeeping.maintenance.view', facilityFeature: MENAGE },
    maintenance_open: { permission: 'housekeeping.maintenance.view', facilityFeature: MENAGE },
    maintenance_critical: { permission: 'housekeeping.maintenance.view', facilityFeature: MENAGE },
    lost_found: { permission: 'housekeeping.lostfound.view', facilityFeature: MENAGE },
    lost_found_unclaimed: { permission: 'housekeeping.lostfound.view', facilityFeature: MENAGE },
} as const satisfies Record<string, AccesModule>;

export type RefusAcces = 'permission' | 'module';

/**
 * Pourquoi ce module est refusé, ou `null` s'il est accessible.
 *
 * `facility` à `null` (établissement pas encore chargé) ne refuse rien sur l'axe
 * module : on ne présente pas une donnée manquante comme un module coupé.
 */
export function refusAccesModule(
    acces: AccesModule,
    hasPermission: (permission: string) => boolean,
    facility: Record<string, unknown> | null | undefined,
): RefusAcces | null {
    const { permission, facilityFeature } = acces;
    if (permission) {
        const permissions: readonly string[] = typeof permission === 'string' ? [permission] : permission;
        if (!permissions.some((p) => hasPermission(p))) return 'permission';
    }
    if (facilityFeature && facility && !facility[facilityFeature]) return 'module';
    return null;
}
